import React, { useEffect, useMemo, useRef, useState } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { ToastCommon } from "../common/Toast";

const extractPublicId = (raw) => {
  const text = String(raw || "");
  const match = text.match(/[a-z0-9]{12}/i);
  return match ? match[0] : null;
};

const parseQrPayload = (raw) => {
  const text = String(raw || "").trim();
  let obj = null;
  try {
    obj = JSON.parse(text);
  } catch {
    obj = null;
  }

  const publicId =
    (obj && (obj.publicId || obj.public_id || obj.recipientPublicId)) || extractPublicId(text);
  const name =
    (obj && (obj.fullName || obj.name || obj.full_name || obj.recipientName)) || null;

  return { publicId: publicId || null, name: name ? String(name) : null, raw: text };
};

const QrScannerModal = ({ isOpen, onClose, onDetected }) => {
  const reader = useMemo(() => new BrowserMultiFormatReader(), []);
  const videoRef = useRef(null);
  const fileInputRef = useRef(null);

  const [mode, setMode] = useState("camera"); // camera | upload
  const [loading, setLoading] = useState(false);
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState("");
  const [cameraActive, setCameraActive] = useState(false);

  // When user selects "Upload image", open gallery/file picker immediately.
  useEffect(() => {
    if (!isOpen) return;
    if (mode !== "upload") return;
    // Small delay ensures the input is mounted before click.
    const t = setTimeout(() => fileInputRef.current?.click(), 0);
    return () => clearTimeout(t);
  }, [isOpen, mode]);

  const stop = async () => {
    try {
      reader.reset();
    } catch {
      // ignore
    }
  };

  const handleDetectedText = async (text) => {
    const parsed = parseQrPayload(text);
    if (!parsed.publicId) {
      ToastCommon.error("QR scanned, but no valid publicId found.");
      return;
    }
    onDetected?.(parsed);
    onClose?.();
  };

  useEffect(() => {
    if (!isOpen) return;
    setCameraActive(true);

    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const list = await BrowserMultiFormatReader.listVideoInputDevices();
        if (cancelled) return;
        setDevices(list || []);
        setDeviceId((list && list[0] && list[0].deviceId) || "");
      } catch (e) {
        console.error(e);
        ToastCommon.error("Could not access camera devices.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (mode !== "camera") {
      stop();
      return;
    }
    if (!cameraActive) {
      stop();
      return;
    }
    if (!videoRef.current) return;

    let active = true;
    setLoading(true);
    const constraints = { video: { facingMode: { ideal: "environment" } }, audio: false };
    reader
      .decodeFromConstraints(constraints, videoRef.current, (result, err) => {
        if (!active) return;
        if (result) {
          const text = result.getText?.() ?? String(result);
          handleDetectedText(text);
        }
        // "NotFoundException" is the normal case when no QR is in frame yet.
        if (err && err?.name !== "NotFoundException") {
          console.error(err);
        }
      })
      .catch((e) => {
        console.error(e);
        const msg = String(e?.message || "");
        if (msg.toLowerCase().includes("only secure origins") || msg.toLowerCase().includes("secure")) {
          ToastCommon.error("Camera requires HTTPS (secure site).");
        } else {
          ToastCommon.error("Camera permission denied or unavailable.");
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mode, cameraActive, reader]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-2xl border border-gray-100 p-5 sm:p-6 m-0 sm:m-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900">Scan QR</h3>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors flex items-center justify-center"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="flex gap-2 mb-4">
          <button
            type="button"
            onClick={() => setMode("camera")}
            className={`flex-1 px-3 py-2 rounded-lg text-sm font-semibold border ${
              mode === "camera"
                ? "bg-orange-50 border-orange-300 text-orange-700"
                : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
            }`}
          >
            Camera
          </button>
          <button
            type="button"
            onClick={() => setMode("upload")}
            className={`flex-1 px-3 py-2 rounded-lg text-sm font-semibold border ${
              mode === "upload"
                ? "bg-orange-50 border-orange-300 text-orange-700"
                : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
            }`}
          >
            Upload image
          </button>
        </div>

        {mode === "camera" && (
          <div className="space-y-3">
            {devices.length > 1 && (
              <div>
                <label className="text-sm font-semibold text-gray-800">Camera</label>
                <select
                  value={deviceId}
                  onChange={(e) => setDeviceId(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
                >
                  {devices.map((d) => (
                    <option key={d.deviceId} value={d.deviceId}>
                      {d.label || "Camera"}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {!cameraActive ? (
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                <p className="text-sm text-gray-800 font-semibold">Camera is off</p>
                <p className="text-xs text-gray-600 mt-1">
                  Tap “Start camera” to scan. (Your browser will ask for camera permission.)
                </p>
                <button
                  type="button"
                  onClick={() => setCameraActive(true)}
                  className="mt-3 w-full px-4 py-2 rounded-lg bg-gradient-to-r from-orange-500 to-yellow-500 text-white font-semibold shadow-md hover:shadow-lg transition-all"
                >
                  Start camera
                </button>
              </div>
            ) : (
              <>
                <div className="rounded-xl overflow-hidden border border-gray-200 bg-black">
                  <video ref={videoRef} className="w-full h-64 object-cover" muted playsInline autoPlay />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs text-gray-600">
                    Point the camera at the user’s QR. We’ll detect a 12-character publicId automatically.
                  </p>
                  <button
                    type="button"
                    onClick={() => setCameraActive(false)}
                    className="shrink-0 px-3 py-2 rounded-lg border border-gray-300 text-gray-700 text-xs font-semibold hover:bg-gray-50"
                  >
                    Stop
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {mode === "upload" && (
          <div className="space-y-3">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (!file) return;
                setLoading(true);
                try {
                  const url = URL.createObjectURL(file);
                  try {
                    const result = await reader.decodeFromImageUrl(url);
                    const text = result.getText?.() ?? String(result);
                    await handleDetectedText(text);
                  } finally {
                    URL.revokeObjectURL(url);
                  }
                } catch (err) {
                  console.error(err);
                  ToastCommon.error("Could not read QR from that image.");
                } finally {
                  setLoading(false);
                }
              }}
            />
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm text-gray-700">
                Choose a QR image from gallery/files.
              </div>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="px-3 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-semibold hover:bg-gray-50"
              >
                Upload
              </button>
            </div>
            <p className="text-xs text-gray-600">
              Upload a screenshot/photo of a QR from gallery or files.
            </p>
          </div>
        )}

        {loading && (
          <p className="text-center text-sm text-gray-500 mt-4">Loading…</p>
        )}
      </div>
    </div>
  );
};

export default QrScannerModal;

